import React, { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { FiX, FiCopy, FiDownload, FiUpload, FiShield, FiCpu, FiRefreshCw } from 'react-icons/fi';
import { useAssetClass } from '../../contexts/AssetClassContext';
import { useTokenIssuer } from '../../contexts/TokenIssuerContext';
import {
  CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID,
  DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID,
  PLATFORM_PREBUILT_ERC1155_TEMPLATE_ID,
  PLATFORM_PREBUILT_ERC20_TEMPLATE_ID,
  resolveContractArtifact,
  suggestStandardContractTemplateId
} from '../../blockchain/standardContractTemplates';
import {
  REFERENCE_ONLY_SOLIDITY_BY_DEPLOY_TOKEN_TYPE_ID,
  REFERENCE_ONLY_SOURCE_FILE_BY_DEPLOY_TOKEN_TYPE_ID
} from '../../blockchain/referenceStandardSoliditySources';
import {
  buildSignerOutputTemplate,
  copyTextToClipboard,
  delay,
  downloadJsonFile,
  formatPreparedPayload,
  parseSignedTransactionInput,
  sanitizeContractName
} from '../../blockchain/offlineSigning';
import { canAuthorizedTokenizerDeployAssetClassContract } from '../../utils/assetClassContractUiAccess';
import { buildSharedSeries721ConstructorParameters } from '../../blockchain/deployConstructorParams';
import { usePersistedOfflineSignerAddress } from '../../hooks/usePersistedOfflineSignerAddress';
import { getOfflineSignerAddressForUser } from '../../api/offlineSignerPreferences';
import { getApiErrorMessage } from '../../api/apiErrors';
import {
  DEPLOY_TOKEN_ARTIFACT_MODE,
  DEPLOY_TOKEN_TYPES,
  deployTokenTypeUsesPlatformPrebuiltArtifact,
  getDeclaredTokenStandardForDeployTokenTypeId,
  getDefaultDeployTokenTypeId,
  getDeployTokenType
} from '../../constants/deployTokenTypes';
import { DeployTokenTypeGrid } from './DeployTokenTypeGrid';
import { ContractSoliditySourcePanel } from './ContractSoliditySourcePanel';
import TransactionLookupDrawer from '../BlockchainExplorer/TransactionLookupDrawer';

const Overlay = styled(motion.div)`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.82);
  backdrop-filter: blur(8px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
  padding: 1.5rem;
`;

const Surface = styled(motion.div)`
  background: #1a1a3e;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 16px;
  width: 100%;
  max-width: min(920px, 100%);
  max-height: min(90vh, 880px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.5);
`;

const Head = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  h2 {
    margin: 0;
    color: #fff;
    font-size: 1.2rem;
    font-weight: 700;
  }
  .sub {
    margin: 0.35rem 0 0;
    color: #94a3b8;
    font-size: 0.88rem;
    line-height: 1.4;
  }
`;

const Body = styled.div`
  padding: 1.25rem 1.5rem;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
`;

const CloseBtn = styled.button`
  background: rgba(255, 255, 255, 0.08);
  border: none;
  border-radius: 8px;
  padding: 0.5rem;
  color: #cbd5e1;
  cursor: pointer;
  &:hover {
    color: #fff;
    background: rgba(255, 255, 255, 0.14);
  }
`;

const FormPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
  color: #94a3b8;
  font-size: 0.85rem;

  span.label {
    color: #e2e8f0;
  }

  input,
  select,
  textarea {
    padding: 0.55rem 0.65rem;
    border-radius: 8px;
    border: 1px solid rgba(255, 255, 255, 0.15);
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    font-size: 0.92rem;
    width: 100%;
    max-width: 100%;
  }

  textarea {
    min-height: 100px;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.8rem;
    resize: vertical;
  }
`;

const FlowSteps = styled.ol`
  margin: 0 0 1rem;
  padding-left: 1.25rem;
  color: #a0a0a0;
  line-height: 1.55;
  font-size: 0.9rem;

  li {
    margin-bottom: 0.5rem;
  }

  strong {
    color: #e2e8f0;
    font-weight: 600;
  }

  code {
    font-size: 0.85em;
    color: #9bd6ff;
  }
`;

const StatusPanel = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  color: #fff;

  .title {
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #4facfe;
  }

  .message {
    color: #a0a0a0;
    line-height: 1.5;
    margin-bottom: 1rem;
  }

  .meta {
    display: flex;
    flex-wrap: wrap;
    gap: 1rem;
    color: #a0a0a0;
    font-size: 0.9rem;
  }
`;

const PayloadBox = styled.div`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;

  .title {
    color: #fff;
    font-weight: 600;
    margin-bottom: 0.5rem;
  }

  .hint {
    color: #a0a0a0;
    font-size: 0.9rem;
    margin-bottom: 1rem;
    line-height: 1.5;
  }

  textarea {
    width: 100%;
    min-height: 160px;
    background: rgba(15, 15, 35, 0.8);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, 0.15);
    border-radius: 8px;
    padding: 1rem;
    resize: vertical;
    font-family: Consolas, Monaco, monospace;
    font-size: 0.85rem;
  }
`;

const PayloadActions = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
  flex-wrap: wrap;
`;

const MiniButton = styled.button.attrs({ type: 'button' })`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.12);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const FilePickLabel = styled.label`
  padding: 0.6rem 0.9rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  cursor: pointer;
  font-size: 0.9rem;

  &:hover {
    background: rgba(255, 255, 255, 0.12);
  }

  input {
    display: none;
  }
`;

const ActionRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  margin-top: 0.5rem;
  align-items: center;
`;

const PrimaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: none;
  border-radius: 8px;
  font-weight: 600;
  background: #4facfe;
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: #3d8bfe;
  }

  &:disabled {
    background: #2a2a3e;
    color: #a0a0a0;
    cursor: not-allowed;
  }
`;

const SecondaryButton = styled.button.attrs({ type: 'button' })`
  padding: 0.75rem 1.25rem;
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 8px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.08);
  color: #fff;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.14);
  }

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const HashButton = styled.button.attrs({ type: 'button' })`
  background: transparent;
  border: none;
  padding: 0;
  color: #4facfe;
  font-family: Consolas, Monaco, monospace;
  cursor: pointer;
  text-decoration: underline;
`;

const Warn = styled.p`
  margin: 0 0 1rem;
  padding: 0.65rem 0.75rem;
  border-radius: 8px;
  background: rgba(251, 191, 36, 0.1);
  border: 1px solid rgba(251, 191, 36, 0.3);
  color: #fcd34d;
  font-size: 0.85rem;
  line-height: 1.45;
`;

function AssetClassDeployContractModal({ assetClass, onClose, onCompleted }) {
  const { prepareContractDeployment, submitContractDeployment, getAssetClassById } = useAssetClass();
  const { currentIssuer, session, isAdmin } = useTokenIssuer();
  const currentUserId = currentIssuer?.userId || session?.userId || currentIssuer?.id || null;
  const deploymentAllowed =
    isAdmin ||
    canAuthorizedTokenizerDeployAssetClassContract(assetClass, currentIssuer?.authorizedTokenizerId ?? null);
  const [signerAddress, setSignerAddress] = usePersistedOfflineSignerAddress(currentUserId);

  const [contractName, setContractName] = useState('');
  const [deployTokenTypeId, setDeployTokenTypeId] = useState('nonFungible');
  const [contractTemplateId, setContractTemplateId] = useState(DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID);
  const [customContractAbiJson, setCustomContractAbiJson] = useState('');
  const [customContractBytecode, setCustomContractBytecode] = useState('');
  const [customMintFunctionName, setCustomMintFunctionName] = useState('mint');
  const [preparedDeployment, setPreparedDeployment] = useState(null);
  const [signedDeploymentInput, setSignedDeploymentInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [submittedTx, setSubmittedTx] = useState(null);
  const [inspectedTxHash, setInspectedTxHash] = useState(null);

  const signerOutputTemplate = JSON.stringify(
    buildSignerOutputTemplate({ summary: 'Asset class contract deployment' }),
    null,
    2
  );

  // Reset only when switching asset class. Including name/category/tokenStandard here cleared a successful
  // prepare after background list/detail refresh (same id, new object field values).
  useEffect(() => {
    if (!assetClass) return;
    setContractName(sanitizeContractName(`${assetClass.name}ClassNFT`));
    setPreparedDeployment(null);
    setSignedDeploymentInput('');
    setSubmittedTx(null);
    const dft = getDefaultDeployTokenTypeId(assetClass);
    setDeployTokenTypeId(dft);
    const t = getDeployTokenType(dft);
    if (t.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC721) {
      setContractTemplateId(suggestStandardContractTemplateId(assetClass.category) || DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID);
    } else if (t.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC20) {
      setContractTemplateId(PLATFORM_PREBUILT_ERC20_TEMPLATE_ID);
    } else if (t.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC1155) {
      setContractTemplateId(PLATFORM_PREBUILT_ERC1155_TEMPLATE_ID);
    } else {
      setContractTemplateId(CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID);
    }
    // Intentionally key off identity (id) only; including `assetClass` would re-reset on unrelated field updates.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- same id keeps stable prepare state across detail refreshes
  }, [assetClass?.id]);

  useEffect(() => {
    if (!assetClass || !currentUserId) return;
    if (getOfflineSignerAddressForUser(currentUserId)) return;
    const fromProfile = String(currentIssuer?.walletAddress || '').trim();
    if (fromProfile) setSignerAddress(fromProfile);
  // eslint-disable-next-line react-hooks/exhaustive-deps -- signer bootstrap when class identity (id) changes
  }, [assetClass?.id, currentUserId, currentIssuer?.walletAddress, setSignerAddress]);

  const handleSelectDeployTokenType = (id) => {
    if (!assetClass) return;
    setPreparedDeployment(null);
    setSignedDeploymentInput('');
    setDeployTokenTypeId(id);
    const t = getDeployTokenType(id);
    if (t.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC721) {
      setContractTemplateId(suggestStandardContractTemplateId(assetClass.category) || DEFAULT_STANDARD_CONTRACT_TEMPLATE_ID);
    } else if (t.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC20) {
      setContractTemplateId(PLATFORM_PREBUILT_ERC20_TEMPLATE_ID);
    } else if (t.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC1155) {
      setContractTemplateId(PLATFORM_PREBUILT_ERC1155_TEMPLATE_ID);
    } else {
      setContractTemplateId(CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID);
    }
  };

  const resetOfflineFlow = () => {
    setPreparedDeployment(null);
    setSignedDeploymentInput('');
  };

  const selectedDeployTokenType = getDeployTokenType(deployTokenTypeId);
  const usesPlatformPrebuilt = deployTokenTypeUsesPlatformPrebuiltArtifact(selectedDeployTokenType);
  const isPrebuilt721 = selectedDeployTokenType?.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC721;

  const soliditySourcePanel = useMemo(() => {
    const refSource = REFERENCE_ONLY_SOLIDITY_BY_DEPLOY_TOKEN_TYPE_ID[deployTokenTypeId];
    const refPath = REFERENCE_ONLY_SOURCE_FILE_BY_DEPLOY_TOKEN_TYPE_ID[deployTokenTypeId];
    if (refSource) {
      return {
        kind: 'reference',
        source: refSource,
        fileName: refPath ? refPath.split('/').pop() : undefined,
        title: 'Solidity reference (read-only)'
      };
    }
    if (!usesPlatformPrebuilt) return null;
    try {
      const art = resolveContractArtifact(contractTemplateId, {
        abiJson: customContractAbiJson,
        bytecode: customContractBytecode,
        mintFunctionName: customMintFunctionName
      });
      if (!art?.soliditySource) return null;
      return {
        kind: 'prebuilt',
        source: art.soliditySource,
        fileName: art.sourceFileName,
        title: 'Contract source (Solidity)'
      };
    } catch {
      return null;
    }
  }, [
    deployTokenTypeId,
    usesPlatformPrebuilt,
    contractTemplateId,
    customContractAbiJson,
    customContractBytecode,
    customMintFunctionName
  ]);

  if (!assetClass) return null;

  const hasDeployed = Boolean(assetClass.contractAddress && String(assetClass.contractAddress).trim());

  const pollForDeploymentConfirmation = async (assetClassId) => {
    for (let attempt = 0; attempt < 12; attempt += 1) {
      const refreshed = await getAssetClassById(assetClassId, true);
      if (refreshed?.contractAddress) {
        return refreshed;
      }
      await delay(5000);
    }
    return getAssetClassById(assetClassId, true);
  };

  const handleCopyPayload = async (payload) => {
    await copyTextToClipboard(formatPreparedPayload(payload));
    toast.success('Copied JSON');
  };

  const handlePrepare = async () => {
    if (!deploymentAllowed) {
      toast.error('You cannot deploy a contract for this asset class.');
      return;
    }
    if (!currentUserId) {
      toast.error('Please sign in before preparing deployment.');
      return;
    }
    if (!signerAddress.trim()) {
      toast.error('Offline signer address is required.');
      return;
    }
    let contractArtifact;
    try {
      contractArtifact = resolveContractArtifact(contractTemplateId, {
        abiJson: customContractAbiJson,
        bytecode: customContractBytecode,
        mintFunctionName: customMintFunctionName
      });
    } catch (e) {
      toast.error(e.message || 'Invalid contract template');
      return;
    }
    const bc = String(contractArtifact.bytecode || '').trim();
    if (!bc.startsWith('0x') || bc.length < 4) {
      toast.error('Creation bytecode (0x…) is required to deploy (use a standard template or paste bytecode for custom).');
      return;
    }

    setBusy(true);
    try {
      const name = sanitizeContractName(contractName || `${assetClass.name}ClassNFT`);
      const constructorParameters = await buildSharedSeries721ConstructorParameters({
        contractTemplateId,
        isAdmin: Boolean(isAdmin),
        isPlatformStandard: Boolean(assetClass.isPlatformStandard),
        assetClass,
        contractName: name,
        tokenizerWalletAddresses: [currentIssuer?.walletAddress, signerAddress]
          .map((w) => String(w || '').trim())
          .filter(Boolean)
      });
      const deploymentPayload = await prepareContractDeployment(assetClass.id, {
        assetClassId: assetClass.id,
        contractName: name,
        signerAddress: signerAddress.trim(),
        contractAbi: contractArtifact.abiJson,
        bytecode: contractArtifact.bytecode,
        constructorParameters
      });
      const txData = String(deploymentPayload?.data ?? '').trim();
      if (deploymentPayload == null || typeof deploymentPayload !== 'object' || !txData) {
        toast.error(
          'Prepare deployment returned an empty or invalid payload (missing transaction data). Check the network response for this request.'
        );
        return;
      }
      setPreparedDeployment(deploymentPayload);
      if (
        isAdmin &&
        assetClass.isPlatformStandard &&
        contractTemplateId !== CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID
      ) {
        try {
          const n = JSON.parse(constructorParameters[2] || '[]').length;
          toast.success(`Unsigned deployment ready. ${n} approved tokenizer wallet(s) pre-authorized as on-chain minters.`);
        } catch {
          toast.success('Unsigned deployment transaction ready.');
        }
      } else {
        toast.success('Unsigned deployment transaction ready.');
      }
    } catch (e) {
      toast.error(getApiErrorMessage(e, 'Prepare deployment failed'));
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitSigned = async () => {
    if (!deploymentAllowed) {
      toast.error('You cannot deploy a contract for this asset class.');
      return;
    }
    setBusy(true);
    try {
      let contractArtifact;
      try {
        contractArtifact = resolveContractArtifact(contractTemplateId, {
          abiJson: customContractAbiJson,
          bytecode: customContractBytecode,
          mintFunctionName: customMintFunctionName
        });
      } catch (e) {
        toast.error(e.message || 'Invalid contract template');
        return;
      }
      const signedDeployment = parseSignedTransactionInput(signedDeploymentInput);
      const name = sanitizeContractName(contractName || `${assetClass.name}ClassNFT`);
      const declaredTokenStandard = getDeclaredTokenStandardForDeployTokenTypeId(deployTokenTypeId);
      const deploymentResponse = await submitContractDeployment(assetClass.id, {
        assetClassId: assetClass.id,
        contractName: name,
        signedTransactionHex: signedDeployment.signedTransactionHex,
        expectedContractAddress: signedDeployment.expectedContractAddress,
        contractAbi: contractArtifact.abiJson,
        bytecode: null,
        constructorParameters: null,
        ...(declaredTokenStandard ? { declaredTokenStandard } : {})
      });
      const txHash = deploymentResponse.deploymentTransactionHash || deploymentResponse.DeploymentTransactionHash || '';
      toast.success(txHash ? `Broadcast submitted. Tx: ${txHash}` : 'Deployment submitted.');
      resetOfflineFlow();
      await pollForDeploymentConfirmation(assetClass.id);
      onCompleted?.();
      setSubmittedTx({ hash: txHash, status: deploymentResponse.status || deploymentResponse.Status || 'submitted' });
    } catch (e) {
      toast.error(e.response?.data?.message || e.message || 'Submit deployment failed');
    } finally {
      setBusy(false);
    }
  };

  const handleSignedFile = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '').trim();
      if (!text) {
        toast.error('File was empty');
        return;
      }
      setSignedDeploymentInput(text);
      toast.success(`Loaded ${file.name}`);
    };
    reader.onerror = () => toast.error('Could not read file');
    reader.readAsText(file, 'utf-8');
    event.target.value = '';
  };

  return (
    <>
      <TransactionLookupDrawer transactionHash={inspectedTxHash} onClose={() => setInspectedTxHash(null)} />
      <AnimatePresence>
      <Overlay
        key={assetClass.id || 'deploy-contract'}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={(e) => e.target === e.currentTarget && !busy && onClose()}
      >
        <Surface
          initial={{ scale: 0.94, opacity: 0, y: 12 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.94, opacity: 0, y: 12 }}
          onClick={(e) => e.stopPropagation()}
        >
          <Head>
            <div>
              <h2>
                <FiShield style={{ verticalAlign: '-3px', marginRight: '0.35rem', color: '#4facfe' }} />
                Deploy smart contract
              </h2>
              <p className="sub">{assetClass.name}</p>
            </div>
            <CloseBtn type="button" onClick={onClose} aria-label="Close" disabled={busy}>
              <FiX size={22} />
            </CloseBtn>
          </Head>

          <Body>
            {hasDeployed ? (
              <>
                <p style={{ color: '#94a3b8', margin: '0 0 1rem', lineHeight: 1.5 }}>
                  This asset class already has an on-chain contract. Close this dialog and use the card for address,
                  explorer link, and deployment time.
                </p>
                <Field>
                  <span className="label">Contract address</span>
                  <input readOnly value={assetClass.contractAddress} />
                </Field>
                <ActionRow>
                  <MiniButton
                    type="button"
                    onClick={() => {
                      copyTextToClipboard(assetClass.contractAddress);
                      toast.success('Copied');
                    }}
                  >
                    <FiCopy /> Copy address
                  </MiniButton>
                </ActionRow>
              </>
            ) : submittedTx ? (
              <StatusPanel>
                <div className="title">Deployment submitted</div>
                <div className="message">
                  The Core Tokenizer backend broadcast the signed deployment via <code>xcb_sendRawTransaction</code> to
                  the Ping chain. Open the hash below to inspect the receipt, or close when done.
                </div>
                <div className="meta">
                  {submittedTx.hash ? (
                    <span>
                      Transaction:{' '}
                      <HashButton type="button" onClick={() => setInspectedTxHash(submittedTx.hash)}>
                        {submittedTx.hash}
                      </HashButton>
                    </span>
                  ) : (
                    <span>Transaction hash pending from API</span>
                  )}
                </div>
                <ActionRow style={{ marginTop: '1rem' }}>
                  <PrimaryButton type="button" onClick={() => onClose()}>
                    Close
                  </PrimaryButton>
                </ActionRow>
              </StatusPanel>
            ) : (
              <>
                <FormPanel>
                  <DeployTokenTypeGrid
                    types={DEPLOY_TOKEN_TYPES}
                    selectedId={deployTokenTypeId}
                    onSelect={handleSelectDeployTokenType}
                  />
                  {assetClass.tokenStandard === 'ERC1155' && deployTokenTypeId !== 'fractionalNFT' && (
                    <Warn>
                      This asset class is configured for <strong>ERC-1155 (CBC1155)</strong>. Choose the CBC1155 card unless
                      you intentionally deploy a different standard.
                    </Warn>
                  )}
                  {assetClass.tokenStandard !== 'ERC1155' && deployTokenTypeId === 'fractionalNFT' && (
                    <Warn>
                      This asset class is <strong>ERC-721</strong> in catalog metadata. A CBC1155 deployment may not match
                      the class — confirm with your administrator before proceeding.
                    </Warn>
                  )}
                  {!usesPlatformPrebuilt && (
                    <Warn>
                      <strong>{selectedDeployTokenType.brandStandard}</strong> requires your own creation bytecode and ABI.
                      Paste audited artifacts below. The platform preloads bytecode for CBC721, CBC20, and CBC1155 catalog
                      contracts; other grid cards ship Solidity references only until a prebuilt package is added.
                    </Warn>
                  )}
                  {isAdmin && assetClass.isPlatformStandard && usesPlatformPrebuilt && (
                    <Warn>
                      <strong>Platform catalog:</strong> your admin signer is on-chain owner; approved tokenizer wallets are
                      seeded as <strong>minters</strong> who may <strong>mint and burn</strong> on the preloaded CBC721 / CBC20 /
                      CBC1155 contracts. For custom bytecode, mirror <code>owner()</code> plus{' '}
                      <code>minters(address)</code> (or equivalent) so suspend/approve stays aligned with{' '}
                      <code>set-minter/prepare</code> (owner-signed).
                    </Warn>
                  )}
                  {isPrebuilt721 && (
                    <p
                      style={{
                        color: '#64748b',
                        fontSize: '0.82rem',
                        margin: '0 0 1rem',
                        lineHeight: 1.45
                      }}
                    >
                      CBC721 deployment uses the platform&apos;s shared series contract bytecode. The internal template is
                      chosen automatically from this asset class&apos;s category — no second token-type choice is needed.
                    </p>
                  )}
                  {selectedDeployTokenType?.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC20 && (
                    <p
                      style={{
                        color: '#64748b',
                        fontSize: '0.82rem',
                        margin: '0 0 1rem',
                        lineHeight: 1.45
                      }}
                    >
                      CBC20 uses the in-repo <code>RwaSharedErc20</code> catalog contract (18 decimals, owner + minters). The
                      constructor receives the same name, symbol, and initial minter list pattern as CBC721.
                    </p>
                  )}
                  {selectedDeployTokenType?.artifactMode === DEPLOY_TOKEN_ARTIFACT_MODE.PREBUILT_ERC1155 && (
                    <p
                      style={{
                        color: '#64748b',
                        fontSize: '0.82rem',
                        margin: '0 0 1rem',
                        lineHeight: 1.45
                      }}
                    >
                      CBC1155 uses the in-repo <code>RwaSharedErc1155</code> catalog contract. Transfers target EOAs only in
                      this minimal build; extend with a full OpenZeppelin ERC-1155 stack for contract recipients.
                    </p>
                  )}
                  {soliditySourcePanel ? (
                    <ContractSoliditySourcePanel
                      title={soliditySourcePanel.title}
                      fileName={soliditySourcePanel.fileName}
                      source={soliditySourcePanel.source}
                      variant={soliditySourcePanel.kind === 'reference' ? 'reference' : 'prebuilt'}
                      note={
                        soliditySourcePanel.kind === 'reference'
                          ? 'No preloaded creation bytecode for this card yet — compile your own contract or choose Custom and paste ABI + bytecode.'
                          : 'This source is embedded next to the compiled ABI and bytecode shipped in the app; they are generated from the same .sol file.'
                      }
                    />
                  ) : null}
                  <Field>
                    <span className="label">Contract name (on-chain / records)</span>
                    <input value={contractName} onChange={(e) => setContractName(e.target.value)} />
                  </Field>
                  <Field>
                    <span className="label">Offline signer address</span>
                    <input
                      value={signerAddress}
                      onChange={(e) => setSignerAddress(e.target.value)}
                      placeholder="0x…"
                      autoComplete="off"
                    />
                    <span style={{ color: '#64748b', fontSize: '0.78rem' }}>
                      Must match the address used by your offline signer. Remembered for your account in this browser.
                    </span>
                  </Field>
                  {contractTemplateId === CUSTOM_TOKENIZER_CONTRACT_TEMPLATE_ID && (
                    <>
                      <Field>
                        <span className="label">Contract ABI (JSON array)</span>
                        <textarea value={customContractAbiJson} onChange={(e) => setCustomContractAbiJson(e.target.value)} />
                      </Field>
                      <Field>
                        <span className="label">Creation bytecode (0x…)</span>
                        <textarea
                          value={customContractBytecode}
                          onChange={(e) => setCustomContractBytecode(e.target.value)}
                          placeholder="0x"
                        />
                      </Field>
                      <Field>
                        <span className="label">Mint function name</span>
                        <input value={customMintFunctionName} onChange={(e) => setCustomMintFunctionName(e.target.value)} />
                      </Field>
                    </>
                  )}
                </FormPanel>

                <StatusPanel>
                  <div className="title">Offline signing &amp; broadcast (Ping chain)</div>
                  <FlowSteps>
                    <li>
                      <strong>Prepare unsigned deployment</strong> — The API builds the unsigned contract-creation
                      transaction JSON your offline signer needs. Private keys never leave the signer.
                    </li>
                    <li>
                      <strong>Sign</strong> — Copy or download the JSON, then sign with the offline signer address you
                      entered (cold wallet or Ping offline signer app).
                    </li>
                    <li>
                      <strong>Upload or paste signed data</strong> — Load the signer output file or paste JSON / raw{' '}
                      <code>0x</code> <code>signedTransactionHex</code>. Then <strong>Broadcast signed deployment</strong> so
                      the backend submits <code>xcb_sendRawTransaction</code>.
                    </li>
                  </FlowSteps>
                  <div className="meta">
                    <span>Asset class: {assetClass.code || assetClass.name}</span>
                    <span>Token type: {selectedDeployTokenType.brandStandard}</span>
                  </div>
                </StatusPanel>

                {preparedDeployment && (
                  <>
                    <PayloadBox>
                      <div className="title">Unsigned contract deployment transaction</div>
                      <div className="hint">
                        Unsigned transaction for deploying the class NFT contract. Sign offline only — do not sign in the
                        browser.
                      </div>
                      <PayloadActions>
                        <MiniButton type="button" onClick={() => handleCopyPayload(preparedDeployment)}>
                          <FiCopy />
                          Copy JSON
                        </MiniButton>
                        <MiniButton
                          type="button"
                          onClick={() =>
                            downloadJsonFile(
                              `${assetClass.code || 'asset-class'}-deploy-prepared.json`,
                              preparedDeployment
                            )
                          }
                        >
                          <FiDownload />
                          Download JSON
                        </MiniButton>
                      </PayloadActions>
                      <textarea readOnly value={formatPreparedPayload(preparedDeployment)} />
                    </PayloadBox>

                    <PayloadBox>
                      <div className="title">Signer output template</div>
                      <div className="hint">Your signer should produce JSON containing signedTransactionHex.</div>
                      <PayloadActions>
                        <MiniButton
                          type="button"
                          onClick={async () => {
                            await copyTextToClipboard(signerOutputTemplate);
                            toast.success('Copied template');
                          }}
                        >
                          <FiCopy />
                          Copy template
                        </MiniButton>
                      </PayloadActions>
                      <textarea readOnly value={signerOutputTemplate} />
                    </PayloadBox>

                    <PayloadBox>
                      <div className="title">Signed deployment — upload or paste</div>
                      <div className="hint">
                        Upload a JSON file from your signer or paste JSON / raw <code>signedTransactionHex</code>.
                      </div>
                      <PayloadActions>
                        <FilePickLabel>
                          <FiUpload aria-hidden />
                          Upload signed file
                          <input
                            type="file"
                            accept=".json,.txt,text/json,application/json"
                            onChange={handleSignedFile}
                          />
                        </FilePickLabel>
                      </PayloadActions>
                      <textarea
                        value={signedDeploymentInput}
                        onChange={(e) => setSignedDeploymentInput(e.target.value)}
                        placeholder="Paste signedTransactionHex or full signer JSON here…"
                      />
                    </PayloadBox>
                  </>
                )}

                <ActionRow>
                  <SecondaryButton type="button" onClick={resetOfflineFlow} disabled={busy || (!preparedDeployment && !signedDeploymentInput)}>
                    <FiRefreshCw />
                    Clear prepared / signed
                  </SecondaryButton>
                  <PrimaryButton
                    type="button"
                    onClick={handlePrepare}
                    disabled={busy || Boolean(preparedDeployment) || !deploymentAllowed}
                  >
                    <FiCpu />
                    Prepare unsigned deployment
                  </PrimaryButton>
                  <PrimaryButton
                    type="button"
                    onClick={handleSubmitSigned}
                    disabled={busy || !preparedDeployment || !signedDeploymentInput.trim() || !deploymentAllowed}
                  >
                    <FiCpu />
                    Broadcast signed deployment
                  </PrimaryButton>
                </ActionRow>
              </>
            )}
          </Body>
        </Surface>
      </Overlay>
      </AnimatePresence>
    </>
  );
}

export default AssetClassDeployContractModal;
